// K-means palette extraction in perceptual CIE LAB space.
// Returns Promise<Array<{ hex: string, percent: number }>> sorted by frequency.

function rgbToXyz(r, g, b) {
	// sRGB to XYZ (D65)
	r /= 255; g /= 255; b /= 255;
	r = r <= 0.04045 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
	g = g <= 0.04045 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
	b = b <= 0.04045 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);
	const x = r * 0.4124 + g * 0.3576 + b * 0.1805;
	const y = r * 0.2126 + g * 0.7152 + b * 0.0722;
	const z = r * 0.0193 + g * 0.1192 + b * 0.9505;
	return [x, y, z];
}

function xyzToLab(x, y, z) {
	// D65 reference white
	const Xr = 0.95047, Yr = 1.00000, Zr = 1.08883;
	let fx = x / Xr, fy = y / Yr, fz = z / Zr;
	const e = 216/24389, k = 24389/27;
	fx = fx > e ? Math.cbrt(fx) : (k * fx + 16) / 116;
	fy = fy > e ? Math.cbrt(fy) : (k * fy + 16) / 116;
	fz = fz > e ? Math.cbrt(fz) : (k * fz + 16) / 116;
	const L = 116 * fy - 16;
	const a = 500 * (fx - fy);
	const b = 200 * (fy - fz);
	return [L, a, b];
}

function labToRgb(L, a, b) {
	// LAB -> XYZ
	const fy = (L + 16) / 116;
	const fx = fy + a / 500;
	const fz = fy - b / 200;
	const e = 216/24389, k = 24389/27;
	const Xr = 0.95047, Yr = 1.00000, Zr = 1.08883;
	let xr = Math.pow(fx, 3); if (xr <= e) xr = (116 * fx - 16) / k;
	let yr = Math.pow(fy, 3); if (yr <= e) yr = (116 * fy - 16) / k;
	let zr = Math.pow(fz, 3); if (zr <= e) zr = (116 * fz - 16) / k;
	let x = xr * Xr, y = yr * Yr, z = zr * Zr;
	// XYZ -> sRGB
	let r =  3.2406 * x + -1.5372 * y + -0.4986 * z;
	let g = -0.9689 * x +  1.8758 * y +  0.0415 * z;
	let bl = 0.0557 * x + -0.2040 * y +  1.0570 * z;
	const comp = (c) => c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1/2.4) - 0.055;
	r = Math.min(1, Math.max(0, comp(r)));
	g = Math.min(1, Math.max(0, comp(g)));
	bl = Math.min(1, Math.max(0, comp(bl)));
	return [Math.round(r * 255), Math.round(g * 255), Math.round(bl * 255)];
}

function rgbToLab(r, g, b) {
	const [x, y, z] = rgbToXyz(r, g, b);
	return xyzToLab(x, y, z);
}

function hexFromRgb(r, g, b) {
	return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}

function distanceLab(a, b) {
	const dL = a[0] - b[0];
	const da = a[1] - b[1];
	const db = a[2] - b[2];
	return dL * dL + da * da + db * db;
}

function kmeansLab(points, k, maxIter = 20) {
	// Initialize centroids with k-means++ for stability
	const centroids = [];
	centroids.push(points[Math.floor(Math.random() * points.length)]);
	while (centroids.length < k) {
		const distances = points.map(p => {
			let minD = Infinity;
			for (let c of centroids) minD = Math.min(minD, distanceLab(p, c));
			return minD;
		});
		const sum = distances.reduce((a, b) => a + b, 0) || 1;
		let r = Math.random() * sum;
		let idx = 0;
		for (let i = 0; i < distances.length; i++) {
			r -= distances[i];
			if (r <= 0) { idx = i; break; }
		}
		centroids.push(points[idx]);
	}

	let assignments = new Array(points.length).fill(0);
	for (let iter = 0; iter < maxIter; iter++) {
		// Assign
		let changed = 0;
		for (let i = 0; i < points.length; i++) {
			let best = 0, bestD = Infinity;
			for (let c = 0; c < k; c++) {
				const d = distanceLab(points[i], centroids[c]);
				if (d < bestD) { bestD = d; best = c; }
			}
			if (assignments[i] !== best) { assignments[i] = best; changed++; }
		}
		// Recompute centroids
		const sums = new Array(k).fill(0).map(() => [0,0,0]);
		const counts = new Array(k).fill(0);
		for (let i = 0; i < points.length; i++) {
			const a = assignments[i];
			sums[a][0] += points[i][0];
			sums[a][1] += points[i][1];
			sums[a][2] += points[i][2];
			counts[a]++;
		}
		for (let c = 0; c < k; c++) {
			if (counts[c] > 0) {
				centroids[c] = [
					sums[c][0] / counts[c],
					sums[c][1] / counts[c],
					sums[c][2] / counts[c]
				];
			} else {
				// Re-seed empty cluster
				centroids[c] = points[Math.floor(Math.random() * points.length)];
			}
		}
		if (!changed) break;
	}
	return { assignments, centroids };
}

export default function extractMainColors(file, colorCount = 6) {
	return new Promise((resolve, reject) => {
		const img = new window.Image();
		const url = URL.createObjectURL(file);
		img.crossOrigin = 'anonymous';
		img.onload = function() {
			const maxDim = 300;
			const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
			const w = Math.max(1, Math.round(img.width * scale));
			const h = Math.max(1, Math.round(img.height * scale));
			const canvas = document.createElement('canvas');
			canvas.width = w; canvas.height = h;
			const ctx = canvas.getContext('2d');
			ctx.drawImage(img, 0, 0, w, h);
			const { data } = ctx.getImageData(0, 0, w, h);

			// Build LAB sample set; ignore near-transparent pixels; optional background clipping
			const points = [];
			const sampleStride = 4 * 2; // take every 2nd pixel
			for (let i = 0; i < data.length; i += sampleStride) {
				const r = data[i];
				const g = data[i + 1];
				const b = data[i + 2];
				const a = data[i + 3];
				if (a < 15) continue;
				// Skip pure blacks/whites too dominant? Keep but lightly quantize by rounding LAB
				const [L, A, B] = rgbToLab(r, g, b);
				points.push([Math.round(L), Math.round(A), Math.round(B)]);
			}
			if (points.length === 0) { resolve([]); URL.revokeObjectURL(url); return; }

			const k = Math.min(colorCount, Math.max(1, points.length));
			const { assignments, centroids } = kmeansLab(points, k, 24);
			const counts = new Array(k).fill(0);
			for (let idx of assignments) counts[idx]++;

			const total = counts.reduce((a, b) => a + b, 0) || 1;
			const palette = centroids.map((c, i) => {
				const [r, g, b] = labToRgb(c[0], c[1], c[2]);
				return { hex: hexFromRgb(r, g, b), percent: +(counts[i] / total * 100).toFixed(1) };
			}).sort((a, b) => b.percent - a.percent).slice(0, 6); // limit to maximum 6 colors

			resolve(palette);
			URL.revokeObjectURL(url);
		};
		img.onerror = reject;
		img.src = url;
	});
}
